import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api, auth, formatApiErrorDetail } from "../lib/api";
import { LOGO_URL } from "../lib/branding";
import { Lock, Mail, Loader2, AlertCircle } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (auth.getToken()) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      auth.setToken(data.access_token);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-[#0f0f12] border border-zinc-800 text-white placeholder:text-zinc-600 px-4 py-3.5 text-base focus:outline-none focus:border-[#E0B33D] focus:ring-1 focus:ring-[#E0B33D] rounded-none transition-colors";

  return (
    <div className="min-h-screen bg-[#09090b] text-white flex items-center justify-center px-5 py-10 font-[IBM_Plex_Sans]">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-3 mb-8">
          <span className="w-36 h-36 shrink-0 inline-block logo-blend">
            <img
              src={LOGO_URL}
              alt="Autohaus Barwinski Logo"
              className="w-full h-full object-cover"
            />
          </span>
          <div className="text-[10px] text-zinc-500 tracking-[0.4em] uppercase">
            Admin-Bereich
          </div>
        </div>

        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="bg-[#0f0f12] border border-zinc-800 p-8 md:p-10"
        >
          <div className="text-[11px] tracking-[0.4em] uppercase gold-text mb-3">
            — Anmeldung
          </div>
          <h1 className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-[0.95]">
            Willkommen
            <br />
            zurück.
          </h1>
          <p className="mt-3 text-sm text-zinc-400">
            Geben Sie Ihre Zugangsdaten ein, um die Terminanfragen zu verwalten.
          </p>

          {error && (
            <div
              data-testid="login-error"
              className="mt-6 flex items-start gap-2 border border-red-900 bg-red-950/40 text-red-200 text-sm px-4 py-3"
            >
              <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="mt-6 space-y-5">
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-400 mb-2">
                E-Mail
              </label>
              <div className="relative">
                <Mail
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500"
                />
                <input
                  data-testid="login-email"
                  type="email"
                  className={inputClass + " pl-12"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-400 mb-2">
                Passwort
              </label>
              <div className="relative">
                <Lock
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500"
                />
                <input
                  data-testid="login-password"
                  type="password"
                  className={inputClass + " pl-12"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>
          </div>

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="mt-8 w-full inline-flex items-center justify-center gap-3 gold-bg disabled:opacity-60 text-black font-semibold px-6 py-4 text-xs tracking-[0.3em] uppercase transition-colors"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Anmelden …
              </>
            ) : (
              "Anmelden"
            )}
          </button>

          <div className="mt-6 text-center">
            <a
              href="/"
              className="text-[11px] tracking-[0.3em] uppercase text-zinc-500 hover:gold-text"
              data-testid="login-back"
            >
              ← Zurück zur Website
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
